import React from 'react';
import {
	Typography,
	RadioGroup,
	FormControlLabel,
	Radio,
	Box,
	TextField,
	InputAdornment,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ContainerBox from 'components/ContainerBox';
import { useTranslation } from 'services/localization/useTranslation';
import { DiscountCodeType, CurrencyObject, PriceRule } from 'common/types';
import MoneyTextField from 'common/components/MoneyTextField';

interface Props {
	priceRule: PriceRule;
	onChange: (priceRule: PriceRule) => void;
	error?: string;
	currency: CurrencyObject;
}

const DiscountType = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { priceRule, onChange, currency, error } = props;

	const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseFloat(e.target.value);
		if (value > 100 || value < 0) return;
		const percentage = isNaN(value) ? 0 : value;
		onChange({
			...priceRule,
			value: percentage,
		});
	};

	const handleFixedAmountChange = (value: number) => {
		onChange({
			...priceRule,
			value,
		});
	};

	const handleCodeTypeChange = (type: DiscountCodeType) => {
		if (priceRule.valueType === type) return;
		onChange({
			valueType: type,
			value: 0,
		});
	};

	return (
		<ContainerBox padding="normal">
			<Box mb={3}>
				<Typography variant="h6">{t('discount.discountTypes.types', 'Type')}</Typography>
			</Box>
			<RadioGroup
				className={classes.discountType}
				value={priceRule.valueType}
				onChange={(e) => handleCodeTypeChange(e.target.value as DiscountCodeType)}
			>
				<FormControlLabel
					value={'percentage' as DiscountCodeType}
					control={<Radio color="primary" />}
					label={t('discount.discountTypes.percentage', 'Percentage')}
					labelPlacement="end"
				/>
				{priceRule.valueType === 'percentage' && (
					<Box mt={1} mb={2} ml={4}>
						<TextField
							autoComplete="off"
							value={priceRule.value === 0 ? '' : priceRule.value}
							onChange={handlePercentageChange}
							margin="none"
							type="number"
							error={Boolean(error) || (!!priceRule.value && isNaN(priceRule.value))}
							helperText={error}
							variant="outlined"
							InputProps={{
								startAdornment: <InputAdornment position="start">%</InputAdornment>,
							}}
						/>
					</Box>
				)}
				<FormControlLabel
					value={'fixed_amount' as DiscountCodeType}
					control={<Radio color="primary" />}
					label={t('discount.discountTypes.fixedAmount', 'Fixed amount')}
					labelPlacement="end"
				/>
				{priceRule.valueType === 'fixed_amount' && (
					<Box mt={1} ml={4}>
						<MoneyTextField
							currency={currency}
							label={t('common:payment.amount', 'Amount')}
							value={priceRule.value}
							useMinorUnits
							onValueChange={handleFixedAmountChange}
							variant="outlined"
							error={Boolean(error)}
							helperText={error}
						/>
					</Box>
				)}
			</RadioGroup>
		</ContainerBox>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		discountType: {
			maxWidth: 300,
			margin: theme.spacing(1, 0, 0, 0),
		},
	}),
);

export default DiscountType;
